/**
 * The card on file (TMMRSL-0046): the card's brand, last four digits, expiry and status in words,
 * with Add / Replace (the one contained button, ACT-001) and Remove (destructive, confirmed —
 * ACT-003; `409 RESELLER_CHARGE_UNPAID` refuses it while a charge is unpaid). OWNER only; the
 * others see both actions disabled with the owner-only tooltip and caption (PERM-002).
 */
import { useState } from 'react';
import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { backendCode } from '@/api/errors';
import { resellerBillingApi, type CardOnFile } from '@/api/resellerBilling';
import { useIsOwner } from '@/auth/authHooks';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { describeApiError } from '@/components/useApiErrorHandler';
import type { RemoteState } from '@/components/useRemote';
import { useToast } from '@/components/useToast';
import { StatusChip } from '@/components/status/StatusChip';
import { AddCardDialog, type AddCardMode } from './AddCardDialog';
import { OwnerOnlyAction } from './OwnerOnlyAction';
import { brandLabel } from './brandLabel';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export interface CardOnFileSectionProps {
  card: RemoteState<CardOnFile | null>;
  hasOpenCharges: boolean;
  /** The collection authorization is active, so removing the card withdraws it. */
  collectionAuthorized: boolean;
  onChanged: () => void;
}

export function CardOnFileSection({
  card,
  hasOpenCharges,
  collectionAuthorized,
  onChanged,
}: CardOnFileSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const isOwner = useIsOwner();
  const [adding, setAdding] = useState(false);
  const [addMode, setAddMode] = useState<AddCardMode>('add');
  const [removing, setRemoving] = useState(false);
  const onFile = card.status === 'ready' ? card.data : null;

  function openAdd(mode: AddCardMode) {
    setAddMode(mode);
    setAdding(true);
  }

  async function remove() {
    await resellerBillingApi.deleteCard();
    toast(t('resellerBilling.remove.done'));
    onChanged();
  }

  function removeError(err: unknown): string {
    return backendCode(err) === 'RESELLER_CHARGE_UNPAID'
      ? t('resellerBilling.remove.unpaid')
      : describeApiError(err, t).message;
  }

  return (
    <Paper id="card-on-file" sx={{ p: 2.5 }}>
      <Typography
        id="card-on-file-heading"
        variant="subtitle1"
        component="h2"
        tabIndex={-1}
        sx={{ '&:focus:not(:focus-visible)': { outline: 'none' } }}
      >
        {t('resellerBilling.card.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t('resellerBilling.card.why')}
      </Typography>
      {card.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={72} />
        </Box>
      ) : null}
      {card.status === 'error' ? <ApiErrorState what={t('errors.what.card')} error={card.error} onRetry={card.reload} /> : null}
      {card.status === 'ready' ? (
        <>
          {onFile ? (
            <CardRow card={onFile} />
          ) : (
            <Typography variant="body2">{t('resellerBilling.card.none')}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
            <OwnerOnlyAction isOwner={isOwner}>
              <Button
                variant="contained"
                disabled={!isOwner}
                onClick={() => openAdd(onFile ? 'replace' : 'add')}
              >
                {onFile ? t('resellerBilling.card.replace') : t('resellerBilling.card.add')}
              </Button>
            </OwnerOnlyAction>
            {onFile ? (
              <OwnerOnlyAction isOwner={isOwner}>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={!isOwner}
                  onClick={() => setRemoving(true)}
                >
                  {t('resellerBilling.card.remove')}
                </Button>
              </OwnerOnlyAction>
            ) : null}
          </Box>
          {isOwner ? null : (
            <Typography variant="caption" component="p" sx={{ mt: 1, color: 'text.secondary' }}>
              {t('resellerBilling.ownerOnly')}
            </Typography>
          )}
        </>
      ) : null}
      <AddCardDialog
        open={adding}
        mode={addMode}
        hasOpenCharges={hasOpenCharges}
        onClose={() => setAdding(false)}
        onDone={onChanged}
      />
      <ConfirmDialog
        open={removing}
        title={
          onFile
            ? t('resellerBilling.remove.title', {
                brand: brandLabel(onFile.brand),
                last4: onFile.last4,
              })
            : t('resellerBilling.remove.titleGeneric')
        }
        consequence={
          collectionAuthorized
            ? `${t('resellerBilling.remove.body')} ${t('resellerBilling.collection.alsoWithdraws')}`
            : t('resellerBilling.remove.body')
        }
        confirmLabel={t('resellerBilling.card.remove')}
        onConfirm={remove}
        destructive
        onCancel={() => setRemoving(false)}
        errorMessage={removeError}
      />
    </Paper>
  );
}

function CardRow({ card }: { card: CardOnFile }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {t('resellerBilling.card.summary', { brand: brandLabel(card.brand), last4: card.last4 })}
      </Typography>
      {card.expMonth !== null && card.expYear !== null ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('resellerBilling.card.expires', { month: pad2(card.expMonth), year: card.expYear })}
        </Typography>
      ) : null}
      <StatusChip domain="card" status={card.status} />
    </Box>
  );
}
